// -------------------------------------------------------------------
// Types and configuration for dynamic form
// -------------------------------------------------------------------

import { safeParseSchemaWithSentry } from "@/features/Sentry/utils";
import { getAuthorized } from "@/lib/fetch";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Control } from "react-hook-form";
import { z } from "zod";
import i18n from "@/i18n";
import { TSections } from "../FormCard/Fields";
import { TTranslation } from "../FormCard/hooks";
import { useEditForm } from "./hooks";

export type TDynamicForm = {
  client: string;
  table_name: string;
  formConfig: TDynamicFormConfig;
  adjustData?: (data: any) => any;
  language?: string;
  adjustOnReset?: (key: string, value: any, fetchedData: any) => any;
  isNew?: boolean;
  fieldsNotApplyTranslation?: ReadonlyArray<string>;
} & (
  | {
      id: number;
    }
  | {
      isNew: true;
    }
);

// Type pour les champs de sélection
type TSelectConfig = {
  [primaryValue: string]: {
    [secondaryValue: string]: string | false;
  };
};

// Type pour les sections
type TSectionConfig = {
  [sectionName: string]: {
    [fieldName: string]: {
      label: string;
      validator: z.ZodTypeAny;
      field?: (control: Control<any>) => React.ReactNode;
    };
  };
};

// Type pour les champs complexes
export type TComplexField = {
  key_config: {
    validator: {
      [key: string]: z.ZodTypeAny;
    };
    primary_key: string;
    secondary_key: string;
  };
  select?: TSelectConfig;
  section?: TSectionConfig;
};

// Type pour la configuration dynamique
export type TDynamicFormConfig = {
  [key: string]: z.ZodTypeAny | TComplexField;
};

export type TSelectFieldConfig = {
  secondary_key: {
    select_field_option: TSections;
    select: TSelectConfig;
    section: TSectionConfig;
  };
};

// -------------------------------------------------------------------
// Generate dynamic schema and conditional validation
// -------------------------------------------------------------------

/**
 * Extracts select fields from the form configuration
 *
 * @param {TDynamicFormConfig} config - The form configuration
 * @returns {Record<string, { primaryKey: string, secondaryKey: string, selectConfig: any, sectionConfig: any }>} The extracted select fields
 */
function extractSelectFields(config: TDynamicFormConfig) {
  const selectFields: Record<
    string,
    {
      primaryKey: string;
      secondaryKey: string;
      selectConfig: any;
      sectionConfig: any;
    }
  > = {};

  Object.entries(config).forEach(([key, value]) => {
    if (typeof value === "object" && value !== null && !("_def" in value)) {
      const complexField = value as any;
      if (
        complexField.key_config &&
        complexField.select &&
        complexField.section
      ) {
        const primaryKey = complexField.key_config.primary_key;
        const secondaryKey = complexField.key_config.secondary_key;

        if (primaryKey && secondaryKey) {
          selectFields[key] = {
            primaryKey,
            secondaryKey,
            selectConfig: complexField.select,
            sectionConfig: complexField.section,
          };
        }
      }
    }
  });

  return selectFields;
}

/**
 * Generates a Zod schema from the form configuration
 *
 * @param {TDynamicFormConfig} config - The form configuration
 * @returns {z.ZodObject<any>} The generated Zod schema
 */
function generateZodSchema(config: TDynamicFormConfig): z.ZodObject<any> {
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  Object.entries(config).forEach(([key, value]) => {
    if ("_def" in value) {
      // It's a simple Zod schema
      schemaFields[key] = value as z.ZodTypeAny;
    } else if (typeof value === "object" && value !== null) {
      // It's a complex field
      const complexField = value as any;

      // Add primary and secondary fields
      if (complexField.key_config && complexField.key_config.validator) {
        Object.entries(complexField.key_config.validator).forEach(
          ([fieldKey, fieldSchema]) => {
            schemaFields[fieldKey] = fieldSchema as z.ZodTypeAny;
          },
        );
      }

      // Add section fields
      if (complexField.section) {
        Object.values(complexField.section).forEach((section: any) => {
          Object.entries(section).forEach(
            ([fieldName, fieldConfig]: [string, any]) => {
              schemaFields[fieldName] = fieldConfig.validator;
            },
          );
        });
      }
    }
  });

  return z.object(schemaFields);
}

/**
 * Validates form data based on the dynamic configuration
 *
 * @param {any} data - The form data
 * @param {TDynamicFormConfig} config - The form configuration
 * @returns {{ success: boolean, message?: string, path?: string }} The validation result
 */
function validateWithDynamicConfig(
  data: any,
  config: TDynamicFormConfig,
): { success: boolean; message?: string; path?: string } {
  // Extract select fields
  const selectFields = extractSelectFields(config);

  // Validate each select field
  for (const [
    _,
    { primaryKey, secondaryKey, selectConfig, sectionConfig },
  ] of Object.entries(selectFields)) {
    const primaryValue = data[primaryKey];
    const secondaryValue = data[secondaryKey];

    if (!primaryValue || !secondaryValue) continue;

    // Check if a section is associated with this combination
    const sectionKey = selectConfig[primaryValue]?.[secondaryValue];
    if (!sectionKey || typeof sectionKey !== "string") continue;

    const sectionValidation = sectionConfig[sectionKey];
    if (!sectionValidation) {
      console.warn(`Section "${sectionKey}" not found in configuration`);
      continue;
    }

    for (const [field, fieldConfig] of Object.entries(sectionValidation)) {
      const result = safeParseSchemaWithSentry(
        (fieldConfig as any).validator,
        data[field],
      );
      if (!result.success) {
        return {
          success: false,
          message: `${
            (fieldConfig as any).label
          } est invalide. Veuillez vérifier ce champ.`,
          path: field,
        };
      }
    }
  }

  return { success: true };
}

/**
 * Advanced dynamic form management hook with conditional validation and field dependencies.
 *
 * This hook extends the capabilities of useEditForm by adding support for:
 * - Dynamic schema generation based on complex configuration objects
 * - Conditional validation rules that depend on field relationships
 * - Field dependencies where the value of one field affects the validation or options of another
 * - Select fields with hierarchical relationships (primary/secondary selection patterns)
 * - Section-based form organization where fields are grouped into logical sections
 *
 * The hook is particularly useful for complex forms where:
 * - The form structure changes based on user selections
 * - Different combinations of fields require different validation rules
 * - Fields have complex interdependencies
 * - The form needs to support both creation and editing workflows
 *
 * @param {TDynamicForm} params - The parameters for the dynamic form
 * @param {string} params.client - The client identifier for API requests
 * @param {string} params.table_name - The database table name for the item
 * @param {TDynamicFormConfig} params.formConfig - Complex configuration object defining the form structure,
 *                                               field relationships, and validation rules
 * @param {(data: any) => any} [params.adjustData] - Optional function to transform data before submission
 * @param {string} [params.language] - Language code for translations (defaults to current UI language)
 * @param {(key: string, value: any) => any} [params.adjustOnReset] - Function to adjust values when form is reset
 * @param {string[]} [params.watchFields=[]] - Additional fields to watch for changes beyond those automatically detected
 * @param {number} [params.id] - The ID of the item to edit (required when not creating a new item)
 * @param {true} [params.isNew] - Flag indicating this is a new item creation (required when not editing)
 *
 * @returns {Object} Enhanced form methods and configuration
 * @returns {Object} returns.form - The form instance from React Hook Form
 * @returns {Function} returns.handleReset - Function to reset the form to its initial state
 * @returns {Function} returns.onSubmit - Function to handle form submission
 * @returns {Function} returns.confirmUpdate - Function to confirm and execute the update/create operation
 * @returns {Function} returns.cancelUpdate - Function to cancel the update/create operation
 * @returns {boolean} returns.isConfirmationModalOpen - Whether the confirmation modal is open
 * @returns {boolean} returns.isDeleteModalOpen - Whether the delete modal is open
 * @returns {Function} returns.openDeleteModal - Function to open the delete modal
 * @returns {Function} returns.closeDeleteModal - Function to close the delete modal
 * @returns {boolean} returns.isUpdating - Whether an update/create operation is in progress
 * @returns {Object|null} returns.changedFields - Fields that have changed (null if no changes)
 * @returns {Object|null} returns.fetchedData - The data fetched from the server (null for new items)
 * @returns {Object} returns.options - Options for select fields based on translations
 * @returns {Object} returns.optionsMap - Comprehensive mapping of all options for select fields with hierarchical relationships
 * @returns {Object} returns.selectFieldsConfig - Configuration for select fields with their associated options and sections
 * @returns {boolean} returns.isLoadingTranslations - Whether translations are currently being loaded
 */
export function useDynamicForm(params: TDynamicForm) {
  const {
    client,
    table_name,
    formConfig,
    adjustData = undefined,
    language,
    adjustOnReset,
    fieldsNotApplyTranslation,
  } = params;

  const effectiveLanguage =
    language ?? (i18n.language?.startsWith("en") ? "en" : "fr");

  // Déterminer si c'est un nouvel élément ou une mise à jour
  const isNew = "isNew" in params;
  const id = "id" in params ? params.id : 0; // Valeur par défaut pour id si isNew est true

  // Retrieve translations to obtain the form labels
  const { data: translations, isLoading: isLoadingTranslations } =
    useQuery<TTranslation>({
      queryKey: [table_name, "translations", effectiveLanguage],
      queryFn: async () => {
        const res = await getAuthorized(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/${client}/${table_name}/translations?language=${effectiveLanguage}`,
        );
        return res.json();
      },
      enabled: true,
    });

  // Extract select fields - this is a pure computation based only on formConfig
  const selectFields = useMemo(
    () => extractSelectFields(formConfig),
    [formConfig],
  );

  // Build options for custom selectors - depends on translations and selectFields
  const optionsMap = useMemo(() => {
    // Initialiser un objet vide pour stocker les options
    const result: Record<string, TSections> = {};

    if (!selectFields || Object.keys(selectFields).length === 0) {
      return result;
    }

    // Create options for custom selectors
    Object.entries(selectFields).forEach(
      ([_, { primaryKey, secondaryKey, selectConfig }]) => {
        if (!selectConfig || Object.keys(selectConfig).length === 0) {
          result[secondaryKey] = [];
          return;
        }

        // Create options for this select field
        result[secondaryKey] = Object.entries(selectConfig).map(
          ([primaryValue, secondaryOptions]) => {
            // Get the translated value or use the primary value by default
            const value =
              translations?.[primaryKey]?.[primaryValue] ?? primaryValue;

            const items =
              typeof secondaryOptions === "object" && secondaryOptions !== null
                ? Object.entries(
                    secondaryOptions as Record<string, string | false>,
                  ).map(([secondaryValue]) => ({
                    value: secondaryValue,
                    label:
                      translations?.[secondaryKey]?.[secondaryValue] ??
                      secondaryValue,
                  }))
                : [];

            return {
              value,
              key: primaryValue,
              items,
            };
          },
        );
      },
    );

    return result;
  }, [translations, selectFields]);

  // Create schema with conditional validation - depends only on formConfig
  const formSchema = useMemo(() => {
    const baseSchema = generateZodSchema(formConfig);

    return baseSchema.superRefine((data, ctx) => {
      const { success, message, path } = validateWithDynamicConfig(
        data,
        formConfig,
      );
      if (!success && path) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: message || "La validation conditionnelle a échoué",
          path: [path],
        });
      }
    });
  }, [formConfig]);

  // Create configuration for asset-specific fields - depends on selectFields and optionsMap
  const selectFieldsConfig = useMemo(() => {
    const result: Record<string, TSelectFieldConfig["secondary_key"]> = {};

    if (!selectFields || Object.keys(selectFields).length === 0) {
      return result;
    }

    Object.entries(selectFields).forEach(
      ([_, { secondaryKey, selectConfig, sectionConfig }]) => {
        const selectFieldOption = optionsMap[secondaryKey] || [];

        result[secondaryKey] = {
          select_field_option: selectFieldOption,
          select: selectConfig || {},
          section: sectionConfig || {},
        };

        if (!optionsMap[secondaryKey]) {
          console.warn(
            `Secondary key "${secondaryKey}" not found in optionsMap, using empty array instead`,
          );
        }
      },
    );

    return result;
  }, [selectFields, optionsMap]);

  // Primary and secondary keys of complex fields must always store raw database keys,
  // never translated labels. Merge them with any caller-provided exclusions.
  const mergedFieldsNotApplyTranslation = useMemo(() => {
    const autoProtected = Object.values(selectFields).flatMap(
      ({ primaryKey, secondaryKey }) => [primaryKey, secondaryKey],
    );
    return Array.from(
      new Set([...autoProtected, ...(fieldsNotApplyTranslation ?? [])]),
    );
  }, [selectFields, fieldsNotApplyTranslation]);

  // Call the existing useEditForm hook with the merged schema
  const formMethods = useEditForm({
    ...(isNew ? { isNew: true } : { id }),
    client,
    table_name,
    FormSchema: formSchema,
    adjustData,
    language: effectiveLanguage,
    adjustOnReset,
    fieldsNotApplyTranslation: mergedFieldsNotApplyTranslation,
    shouldUnregister: isNew ? true : false,
  });

  return {
    ...formMethods,
    optionsMap, // All options for all select fields
    selectFieldsConfig, // Configuration for asset-specific fields
    isLoadingTranslations,
  };
}
